import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { ClientQuestionnaireModel } from '../../../../../../../common/dist/angular/models/client-questionnaire.model';
import {
  ClientQuestionnaireCreationOptions,
} from '../../../../../../../common/src/options/client-questionnaire/client-questionnaire-creation.options';
import { map, switchMap } from 'rxjs/operators';
import {
  ClientQuestionnaireOptions,
} from '../../../../../../../common/src/options/client-questionnaire/client-questionnaire.options';
import { TransformerClassType } from '../../core/interceptors/transformer.http-interceptor';
import { plainToInstance } from 'class-transformer';
import {
  SearchQuery,
} from '../../../../../../../server/dist/angular/server/src/microservice/models/search-query.model';
import {
  ClientQuestionnaireIndexModel,
} from '../../../../../../../common/src/index-models/client-questionnaire.index-model';
import { SearchResult } from '../../../../../../../common/src/models/search-result.model';
import { DataMapper } from '@pristine-ts/data-mapping-common';
import {
  ClientQuestionnaireConvertedToCalculationResponse,
} from '../../../../../../../common/dist/angular/responses/client-questionnaire-converted-to-calculation.response';

@Injectable()
export class ClientQuestionnairesApiController {
  constructor(private readonly httpClient: HttpClient,
              private readonly dataMapper: DataMapper,
              @Inject('environment.api.url') private readonly apiUrl: string) {
  }

  private static map(questionnaire: ClientQuestionnaireModel) {
    return {
      ...questionnaire,
      updatedAt: new Date(questionnaire.updatedAt),
      createdAt: new Date(questionnaire.createdAt),
    };
  }

  public async create(creationOptions: ClientQuestionnaireCreationOptions): Promise<ClientQuestionnaireModel> {
    return this.httpClient.post<ClientQuestionnaireModel>(`${this.apiUrl}/api/client-questionnaires`, creationOptions, {
      context: new HttpContext().set(TransformerClassType, ClientQuestionnaireModel),
    }).toPromise();
  }

  public async get(id: string): Promise<ClientQuestionnaireModel> {
    return this.httpClient.get<ClientQuestionnaireModel>(`${this.apiUrl}/api/client-questionnaires/${id}`, {
      context: new HttpContext().set(TransformerClassType, ClientQuestionnaireModel),
    }).toPromise();
  }

  public async delete(id: string): Promise<ClientQuestionnaireModel> {
    return this.httpClient.delete<ClientQuestionnaireModel>(`${this.apiUrl}/api/client-questionnaires/${id}`, {
      context: new HttpContext().set(TransformerClassType, ClientQuestionnaireModel),
    }).toPromise();
  }

  public async list(): Promise<ClientQuestionnaireModel[]> {
    return this.httpClient.get<{
      'items': ClientQuestionnaireModel[],
      'paginationResult': { 'total': number }
    }>(`${this.apiUrl}/api/client-questionnaires`).pipe(
      map(result => result.items.map(item => plainToInstance(ClientQuestionnaireModel, item))),
    ).toPromise();
  }

  public async updateOptions(id: string, questionnaireOptions: ClientQuestionnaireOptions): Promise<ClientQuestionnaireModel> {
    return this.httpClient.put<ClientQuestionnaireModel>(`${this.apiUrl}/api/client-questionnaires/${id}`, questionnaireOptions, {
      context: new HttpContext().set(TransformerClassType, ClientQuestionnaireModel),
    }).toPromise();
  }

  public async finalize(id: string, questionnaireOptions: ClientQuestionnaireOptions): Promise<ClientQuestionnaireModel> {
    return this.httpClient.post<ClientQuestionnaireModel>(`${this.apiUrl}/api/client-questionnaires/${id}/finalize`, questionnaireOptions, {
      context: new HttpContext().set(TransformerClassType, ClientQuestionnaireModel),
    }).toPromise();
  }

  public async forceFinalize(id: string, questionnaireOptions: ClientQuestionnaireOptions): Promise<ClientQuestionnaireModel> {
    return this.httpClient.post<ClientQuestionnaireModel>(`${this.apiUrl}/api/client-questionnaires/${id}/force-finalize`, questionnaireOptions, {
      context: new HttpContext().set(TransformerClassType, ClientQuestionnaireModel),
    }).toPromise();
  }

  public async convertToCalculation(id: string): Promise<ClientQuestionnaireConvertedToCalculationResponse> {
    return this.httpClient.post<ClientQuestionnaireConvertedToCalculationResponse>(`${this.apiUrl}/api/client-questionnaires/${id}/convert-to-calculation`, {
      context: new HttpContext().set(TransformerClassType, ClientQuestionnaireConvertedToCalculationResponse),
    }).toPromise();
  }

  public async search(searchQuery: SearchQuery): Promise<SearchResult<ClientQuestionnaireIndexModel>> {
    return this.httpClient.get<SearchResult<ClientQuestionnaireIndexModel>>(`${this.apiUrl}/api/client-questionnaires/_search?${(new HttpParams({ fromObject: JSON.parse(JSON.stringify(searchQuery.exportQueryParameters())) })).toString()}`)
      .pipe(
        switchMap(async (response: SearchResult<ClientQuestionnaireIndexModel>) => {
          for (let i = 0; i < response.results.length; i++) {
            response.results[i] = await this.dataMapper.autoMap(response.results[i], ClientQuestionnaireIndexModel);
          }

          return response;
        }),
      )
      .toPromise();
  }
}
